import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type Konva from 'konva';
import type { BoardState } from '../state/board-state.js';
import { zoomAtPointer } from './viewport-math.helper.js';

/**
 * Lives on a Lit host (the whiteboard element) and owns all pan / zoom
 * behaviour:
 *   - Mouse wheel zooms to the pointer (so whatever is under the cursor stays put).
 *   - Hold Space, then drag with the mouse, to pan the canvas.
 *
 * The stage position/scale and the BoardState.viewport are kept in sync:
 * BoardState is the truth, we only push the new viewport to both ends of
 * the pipe on every gesture step. No HistoryController — panning and
 * zooming are *view* operations, they don't belong in undo/redo.
 */
export class ViewportController implements ReactiveController {
  private readonly stage: Konva.Stage;
  private readonly state: BoardState;
  /**
   * The HTML host element; we mirror pan state onto `data-panning` so the
   * CSS cursor rules in whiteboard.css can take over. Keeping styling in
   * the stylesheet (instead of inline `stage.container().style.cursor`)
   * lets the tool cursor and pan cursor co-exist cleanly.
   */
  private readonly panTarget: HTMLElement;

  private spaceDown = false;

  /**
   * True while a pan is armed or running — Space is held, or a pan drag is in progress.
   *
   * Exposed because the pan is not the only thing listening for a press on the canvas, and it
   * is the one that must win. The operator's fixed decision is "hold Space and drag to move the
   * work surface", so that gesture outranks whatever tool happens to be selected. Without this,
   * a placement tool starts drawing an object instead — and worse, silently kills the pan,
   * because its handler calls stopPropagation() on the native event and this controller's
   * mousedown listener sits on the container in the BUBBLE phase, so it is never reached.
   */
  get isPanArmed(): boolean {
    return this.spaceDown || this.panning;
  }
  private panning = false;
  /** Raw client-space pointer position when pan started. */
  private panStart?: { clientX: number; clientY: number; stageX: number; stageY: number };

  constructor(host: ReactiveControllerHost & HTMLElement, stage: Konva.Stage, state: BoardState) {
    this.stage = stage;
    this.state = state;
    this.panTarget = host;
    host.addController(this);
  }

  hostConnected(): void {
    const container = this.stage.container();
    container.addEventListener('wheel', this.onWheel, { passive: false });
    container.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  hostDisconnected(): void {
    const container = this.stage.container();
    container.removeEventListener('wheel', this.onWheel);
    container.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  // ---- zoom -----------------------------------------------------------

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const pointer = this.stage.getPointerPosition();
    if (!pointer) return;

    const direction = e.deltaY < 0 ? 1 : -1;
    const next = zoomAtPointer(this.state.viewport, pointer, direction);
    this.applyViewport(next);
  };

  // ---- pan ------------------------------------------------------------

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code !== 'Space') return;
    if (isEditableTarget(e.target)) return;
    if (this.spaceDown) return;
    this.spaceDown = true;
    this.panTarget.dataset['panning'] = 'ready';
    e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code !== 'Space') return;
    this.spaceDown = false;
    if (!this.panning) delete this.panTarget.dataset['panning'];
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.spaceDown) return;
    this.panning = true;
    this.panTarget.dataset['panning'] = 'grabbing';
    this.panStart = {
      clientX: e.clientX,
      clientY: e.clientY,
      stageX: this.state.viewport.x,
      stageY: this.state.viewport.y,
    };
    e.preventDefault();
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.panning || !this.panStart) return;
    const dx = e.clientX - this.panStart.clientX;
    const dy = e.clientY - this.panStart.clientY;
    this.applyViewport({
      x: this.panStart.stageX + dx,
      y: this.panStart.stageY + dy,
      scale: this.state.viewport.scale,
    });
  };

  private onMouseUp = (): void => {
    if (!this.panning) return;
    this.panning = false;
    this.panStart = undefined;
    if (this.spaceDown) {
      this.panTarget.dataset['panning'] = 'ready';
    } else {
      delete this.panTarget.dataset['panning'];
    }
  };

  // ---- shared ---------------------------------------------------------

  private applyViewport(vp: { x: number; y: number; scale: number }): void {
    this.state.setViewport(vp);
    this.stage.position({ x: vp.x, y: vp.y });
    this.stage.scale({ x: vp.scale, y: vp.scale });
    this.stage.batchDraw();
  }

}

/**
 * True when a keyboard event target is an editable form/text element.
 * Canonical definition: pan/zoom shortcuts and whiteboard-level shortcuts
 * both must skip when the user is typing into an input or contenteditable.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}
